/**
 * Aurora Toast · 对齐 ToVid aurora-kit/ds/feedback.toast
 * 顶部居中、最多 3 条、默认 2400ms；业务侧用 toast.success / info / warning / error
 */

export type AuroraToastTone = "success" | "info" | "warning" | "error" | "loading";

export interface AuroraToastItem {
  /** 唯一 id */
  id: number;
  /** 文案 */
  text: string;
  /** 语气 */
  tone: AuroraToastTone;
}

const DURATION_MS = 2400;
const MAX_TOASTS = 3;

let seq = 0;
let items: AuroraToastItem[] = [];
const subs = new Set<() => void>();

function emit(): void {
  subs.forEach((fn) => fn());
}

function setItems(next: AuroraToastItem[]): void {
  items = next;
  emit();
}

/** 订阅列表变化（供 Host 渲染） */
export function subscribeAuroraToast(listener: () => void): () => void {
  subs.add(listener);
  return () => {
    subs.delete(listener);
  };
}

/** 当前 toast 快照 */
export function getAuroraToasts(): AuroraToastItem[] {
  return items;
}

/** 关闭单条 */
export function dismissAuroraToast(id: number): void {
  setItems(items.filter((item) => item.id !== id));
}

/**
 * 推送全局提示（对齐 ToVid toast）
 * @returns 句柄：update / close
 */
export function toast(text: string, tone: AuroraToastTone = "success") {
  const id = ++seq;
  const list = items;
  const drop = list.length >= MAX_TOASTS ? list[0].id : -1;
  setItems([...list.filter((item) => item.id !== drop), { id, text, tone }]);
  if (tone !== "loading") {
    window.setTimeout(() => dismissAuroraToast(id), DURATION_MS);
  }
  return {
    id,
    update(nextText: string, nextTone: AuroraToastTone = "success") {
      setItems(items.map((item) => (item.id === id ? { ...item, text: nextText, tone: nextTone } : item)));
      window.setTimeout(() => dismissAuroraToast(id), DURATION_MS);
    },
    close: () => dismissAuroraToast(id),
  };
}

toast.success = (text: string) => toast(text, "success");
toast.info = (text: string) => toast(text, "info");
toast.warning = (text: string) => toast(text, "warning");
toast.error = (text: string) => toast(text, "error");
toast.loading = (text: string) => toast(text, "loading");
